/**
 * Hooks React Query para o CRM de leads da Karla (Superadmin).
 *
 * Escritas via service_role implícito pela RLS — o Superadmin tem
 * policies permissivas em `public.leads` e `public.lead_activities`
 * (migration 0011 + 0021). Reads filtram `deleted_at IS NULL`.
 *
 * Mutations invalidam a lista + detalhe afetados para manter a UI
 * consistente. Activities e status vêm via audit trigger + transições
 * de stage disparadas por edge functions, então não precisamos popular
 * tudo manualmente.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { supabase } from '../../lib/supabase.js';

import { computeProvisionPlan, type ProvisionPlan } from './provision-plan.js';
import {
  leadActivitySchema,
  leadSchema,
  markLostInputSchema,
  type CreateLeadInput,
  type Lead,
  type LeadActivity,
  type LeadStage, // usado nas keys e no tipo do filtro
  type MarkLostInput,
} from './schemas.js';

export const LEAD_KEYS = {
  list: (stages: readonly LeadStage[] | 'all' = 'all') => ['leads', 'list', stages] as const,
  detail: (id: string) => ['leads', 'detail', id] as const,
  activities: (leadId: string) => ['leads', 'activities', leadId] as const,
  signedContract: (leadId: string) => ['leads', 'signedContract', leadId] as const,
  provisionedClinic: (leadId: string) => ['leads', 'provisionedClinic', leadId] as const,
};

const leadsListSchema = z.array(leadSchema);
const leadActivitiesListSchema = z.array(leadActivitySchema);

// ---------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------

/**
 * Lista leads ativos. `stages = 'all'` traz tudo (inclusive provisioned
 * e lost); passa array pra filtrar. Default do consumidor: mostra apenas
 * leads "trabalháveis" (ACTIVE_LEAD_STAGES) na tela principal e oferece
 * toggle pra exibir arquivados.
 */
export function useLeads(stages: readonly LeadStage[] | 'all' = 'all') {
  return useQuery({
    queryKey: LEAD_KEYS.list(stages),
    staleTime: 30_000,
    queryFn: async (): Promise<Lead[]> => {
      let query = supabase
        .from('leads')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (stages !== 'all') {
        query = query.in('stage', stages as LeadStage[]);
      }

      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return leadsListSchema.parse(data);
    },
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: id ? LEAD_KEYS.detail(id) : ['leads', 'detail', 'pending'],
    enabled: !!id,
    staleTime: 30_000,
    queryFn: async (): Promise<Lead | null> => {
      // `.maybeSingle()` em vez de `.single()` pra não gerar HTTP 406
      // quando lead não existe ou está soft-deleted (caso reportado
      // pelo Alexandre 2026-04-24: abrir detalhe de lead excluído
      // disparava 406 no console). A página (`LeadDetailPage`) já trata
      // `!lead.data` mostrando "Lead não encontrado".
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('id', id!)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      return leadSchema.parse(data);
    },
  });
}

export function useLeadActivities(leadId: string | undefined) {
  return useQuery({
    queryKey: leadId ? LEAD_KEYS.activities(leadId) : ['leads', 'activities', 'pending'],
    enabled: !!leadId,
    staleTime: 15_000,
    queryFn: async (): Promise<LeadActivity[]> => {
      const { data, error } = await supabase
        .from('lead_activities')
        .select('*')
        .eq('lead_id', leadId!)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return leadActivitiesListSchema.parse(data);
    },
  });
}

// ---------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateLeadInput): Promise<Lead> => {
      const { data: userData } = await supabase.auth.getUser();
      const ownerId = userData.user?.id ?? null;

      const { data, error } = await supabase
        .from('leads')
        .insert({
          full_name: input.full_name,
          legal_name: input.legal_name,
          clinic_name_guess: input.clinic_name_guess,
          email: input.email,
          phone: input.phone,
          cnpj: input.cnpj,
          address: input.address,
          specialty: input.specialty,
          source: input.source,
          notes: input.notes,
          owner_id: ownerId,
        } as never)
        .select('*')
        .single();
      if (error) throw new Error(error.message);
      return leadSchema.parse(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads', 'list'] });
    },
  });
}

/**
 * Atualiza dados cadastrais do lead. Aceita qualquer subconjunto de
 * `CreateLeadInput` — mesmo form do cadastro, mas editável. Não toca
 * em `stage` / `lost_reason` (esses têm hooks dedicados).
 */
export function useUpdateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<CreateLeadInput>): Promise<Lead> => {
      const { id, ...patch } = input;
      const { data, error } = await supabase
        .from('leads')
        .update(patch as never)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw new Error(error.message);
      return leadSchema.parse(data);
    },
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.detail(lead.id) });
      qc.invalidateQueries({ queryKey: ['leads', 'list'] });
    },
  });
}

/**
 * Soft-delete — set `deleted_at = now()`. Hard-delete seria destrutivo
 * demais (audit_trigger + timeline de lead_activities perderiam o
 * contexto). Queries de read já filtram `deleted_at IS NULL`, então o
 * lead some da UI — mas permanece em banco para auditoria e LGPD.
 *
 * Para direito ao esquecimento LGPD (requisição formal), criar RPC
 * dedicada `lgpd_purge_lead(id)` que anonimize PII em vez de deletar.
 */
export function useSoftDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const { error } = await supabase
        .from('leads')
        .update({ deleted_at: new Date().toISOString() } as never)
        .eq('id', id);
      if (error) throw new Error(error.message);
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: ['leads', 'list'] });
    },
  });
}

export function useUpdateLeadNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; notes: string | null }): Promise<Lead> => {
      const { data, error } = await supabase
        .from('leads')
        .update({ notes: input.notes } as never)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw new Error(error.message);
      return leadSchema.parse(data);
    },
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.detail(lead.id) });
      qc.invalidateQueries({ queryKey: ['leads', 'list'] });
    },
  });
}

/**
 * Marca lead como perdido. Preenche `stage='lost'`, `lost_reason` e
 * registra uma activity `stage_changed` com a razão em `metadata`
 * (o audit trigger também cria uma, este INSERT é complementar para
 * capturar o motivo estruturado).
 */
export function useMarkLost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MarkLostInput): Promise<Lead> => {
      markLostInputSchema.parse(input); // validação extra

      const { data: userData } = await supabase.auth.getUser();
      const actorId = userData.user?.id ?? null;

      const { data: updated, error: updateError } = await supabase
        .from('leads')
        .update({
          stage: 'lost',
          lost_reason: input.reason,
          metadata: {
            lost_reason_detail: input.reason_detail,
            lost_at: new Date().toISOString(),
          },
        } as never)
        .eq('id', input.lead_id)
        .select('*')
        .single();
      if (updateError) throw new Error(updateError.message);

      await supabase.from('lead_activities').insert({
        lead_id: input.lead_id,
        actor_id: actorId,
        activity_type: 'stage_changed',
        description: `Lead marcado como perdido: ${input.reason}${
          input.reason_detail ? ` — ${input.reason_detail}` : ''
        }`,
        metadata: { reason: input.reason, reason_detail: input.reason_detail },
      } as never);

      return leadSchema.parse(updated);
    },
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.detail(lead.id) });
      qc.invalidateQueries({ queryKey: LEAD_KEYS.activities(lead.id) });
      qc.invalidateQueries({ queryKey: ['leads', 'list'] });
    },
  });
}

/**
 * Dispara o edge function `send-onboarding-link`. A UI da Karla chama
 * este hook quando ela clica em "Enviar questionário" no detalhe do
 * lead. O template_id é resolvido no backend pelo próprio send-onboarding-link
 * assim que o template default estiver seedado (PR-M1-01b). Por ora
 * este hook aceita o template_id como parâmetro — em M1-01b, passamos
 * o UUID do template seedado diretamente.
 */
export function useSendOnboardingLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { lead_id: string; template_id: string }) => {
      const { data, error } = await supabase.functions.invoke('send-onboarding-link', {
        body: {
          lead_id: input.lead_id,
          template_id: input.template_id,
          expires_in_days: 14,
        },
      });
      if (error) throw new Error(error.message);
      return data as {
        status: string;
        mode: string;
        submission_id?: string;
        public_url?: string;
        expires_at?: string;
        email_status?: 'sent' | 'failed' | 'skipped';
      };
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.detail(variables.lead_id) });
      qc.invalidateQueries({ queryKey: LEAD_KEYS.activities(variables.lead_id) });
      qc.invalidateQueries({ queryKey: ['leads', 'list'] });
    },
  });
}

// `countLeadsByStage` foi movido para `./schemas.ts` — util puro,
// fora desta camada de queries. Quem precisa, importa do barrel
// `./index.js` ou direto de `./schemas.js`. Mudança evita que
// `leads.smoke.test.tsx` precise do supabase client só para testar
// contagem pura (o Test job do CI não define VITE_SUPABASE_*).

/**
 * Acha o contrato `signed` mais recente do lead. Usado pelo
 * `LeadCustomerBanner` pra ancorar o botão "Provisionar agora" no
 * `contract.id` correto (provision-clinic edge fn precisa do ID).
 *
 * Retorna `null` quando não há contrato signed (lead não chegou nesse
 * estado ainda) ou quando o webhook ainda não atualizou a tabela.
 *
 * Encadeia leads → proposals → contracts. Filtra `status = 'signed'`,
 * ordena por `signed_at DESC` e pega o primeiro. Em prod ESPERAMOS um
 * único signed por lead, mas o filtro+order é defensivo.
 */
export function useLatestSignedContract(leadId: string | undefined) {
  return useQuery({
    queryKey: LEAD_KEYS.signedContract(leadId ?? ''),
    enabled: Boolean(leadId),
    staleTime: 60_000,
    queryFn: async (): Promise<SignedContractContext | null> => {
      if (!leadId) return null;
      // `proposals` NÃO tem coluna `deleted_at` (migration 0046) —
      // soft-delete via status='archived'. Não filtra aqui porque
      // contract com status='signed' pressupõe proposal não-arquivada.
      const proposalsRes = await supabase
        .from('proposals')
        .select(
          'id, proposal_type, trial_days, custom_price_brl, discount_percent, ' +
            'saas_free_during_consulting, consulting_duration_months, ' +
            'consulting_installments_count, consulting_installment_brl, plan_id',
        )
        .eq('lead_id', leadId);
      if (proposalsRes.error) throw new Error(proposalsRes.error.message);
      // Cast via `unknown` é necessário pra TS strict — o Supabase JS
      // tipa data como `GenericStringError[] | null` em alguns casos
      // que não consegue inferir. A query está validada manualmente.
      const proposals = (proposalsRes.data ?? []) as unknown as {
        id: string;
        proposal_type: string | null;
        trial_days: number | null;
        custom_price_brl: number | null;
        discount_percent: number | null;
        saas_free_during_consulting: boolean | null;
        consulting_duration_months: number | null;
        consulting_installments_count: number | null;
        consulting_installment_brl: number | null;
        plan_id: string;
      }[];
      if (proposals.length === 0) return null;

      const proposalIds = proposals.map((p) => p.id);
      const contractsRes = await supabase
        .from('contracts')
        .select('id, signed_at, proposal_id')
        .in('proposal_id', proposalIds)
        .eq('status', 'signed')
        .order('signed_at', { ascending: false })
        .limit(1);
      if (contractsRes.error) throw new Error(contractsRes.error.message);
      const row = (contractsRes.data ?? [])[0] as
        | { id: string; signed_at: string | null; proposal_id: string }
        | undefined;
      if (!row) return null;

      const proposal = proposals.find((p) => p.id === row.proposal_id);
      const planRes = proposal
        ? await supabase
            .from('plans')
            .select('monthly_price_brl, trial_days, billing_cycle')
            .eq('id', proposal.plan_id)
            .maybeSingle()
        : null;
      const plan = (planRes?.data ?? null) as {
        monthly_price_brl: number;
        trial_days: number | null;
        billing_cycle: string;
      } | null;

      // Calcula o plano de provisionamento usando o mesmo helper que o
      // edge function usa — single source of truth da regra de negócio
      // (preço efetivo, trial efetivo, status inicial, gate mode).
      const provisionPlan = proposal
        ? computeProvisionPlan({
            proposal_type: proposal.proposal_type ?? 'saas',
            custom_price_brl: proposal.custom_price_brl,
            discount_percent: proposal.discount_percent,
            trial_days: proposal.trial_days,
            saas_free_during_consulting: proposal.saas_free_during_consulting,
            consulting_duration_months: proposal.consulting_duration_months,
            consulting_installments_count: proposal.consulting_installments_count,
            consulting_installment_brl: proposal.consulting_installment_brl,
            plan_monthly_price_brl: Number(plan?.monthly_price_brl ?? 0),
            plan_trial_days: plan?.trial_days ?? null,
          })
        : null;

      return {
        contract_id: row.id,
        signed_at: row.signed_at ?? null,
        proposal_id: row.proposal_id,
        proposal_type: proposal?.proposal_type ?? null,
        billing_cycle: plan?.billing_cycle ?? 'monthly',
        provision_plan: provisionPlan,
      };
    },
  });
}

export interface SignedContractContext {
  contract_id: string;
  signed_at: string | null;
  proposal_id: string;
  proposal_type: string | null;
  billing_cycle: string;
  /** Plano de provisionamento computado — null em casos de borda
   * (proposta perdida no JOIN). UI usa pra renderizar dialog correto. */
  provision_plan: ProvisionPlan | null;
}

/**
 * Acha o `clinic_id` provisionado a partir de um lead. Usado pelo
 * `LeadCustomerBanner` pra ancorar o botão "Acessar painel da clínica"
 * quando o lead atingiu `stage='provisioned'`.
 *
 * Estratégia: olha em `clinic_provisioning_events` o evento
 * `provisioning_complete` mais recente do contrato signed do lead.
 * Vai pelo lead → proposals → contracts → events porque a tabela
 * `clinics` ainda não tem FK de origem (lead_id) — a info da origem
 * mora em `clinic_provisioning_events.contract_id` + metadata.
 *
 * Retorna `null` quando o lead ainda não foi provisionado ou quando
 * não há evento de sucesso registrado (caso em que o backfill manual
 * criou a clínica fora do flow).
 */
export function useProvisionedClinicForLead(leadId: string | undefined) {
  return useQuery({
    queryKey: LEAD_KEYS.provisionedClinic(leadId ?? ''),
    enabled: Boolean(leadId),
    staleTime: 60_000,
    queryFn: async (): Promise<{
      clinic_id: string;
      /** Status do owner member ('invited' = aguardando aceite,
       * 'active' = aceitou, null = não há owner member). Usado pelo
       * banner do lead detail pra escolher entre "Reenviar email de
       * ativação" e "Acessar painel da clínica". */
      owner_member_status: 'invited' | 'active' | 'suspended' | null;
      /** Status da subscription da clínica — diferencia entre
       * "aguardando pagamento da 1ª parcela" (incomplete, gate ativo)
       * e "aguardando aceite do convite" (active, owner ainda não
       * clicou no link). Critico pro banner orientar a Karla:
       * "cobrar pagamento" vs "esperar lead clicar no link". */
      subscription_status:
        | 'incomplete'
        | 'trialing'
        | 'active'
        | 'past_due'
        | 'canceled'
        | 'paused'
        | null;
    } | null> => {
      if (!leadId) return null;
      // 1. Pega proposals do lead. Nota: `proposals` não tem coluna
      // `deleted_at` — não filtra (ver useLatestSignedContract pra detalhes).
      const proposalsRes = await supabase.from('proposals').select('id').eq('lead_id', leadId);
      if (proposalsRes.error) throw new Error(proposalsRes.error.message);
      const proposalIds = (proposalsRes.data ?? []).map((p) => p.id as string);
      if (proposalIds.length === 0) return null;

      // 2. Pega contracts dessas proposals
      const contractsRes = await supabase
        .from('contracts')
        .select('id')
        .in('proposal_id', proposalIds);
      if (contractsRes.error) throw new Error(contractsRes.error.message);
      const contractIds = (contractsRes.data ?? []).map((c) => c.id as string);
      if (contractIds.length === 0) return null;

      // 3. Pega o último provisioning_complete success desses contracts
      const eventRes = await supabase
        .from('clinic_provisioning_events')
        .select('clinic_id')
        .in('contract_id', contractIds)
        .eq('step', 'provisioning_complete')
        .eq('status', 'success')
        .order('created_at', { ascending: false })
        .limit(1);
      if (eventRes.error) throw new Error(eventRes.error.message);
      const row = (eventRes.data ?? [])[0] as { clinic_id: string } | undefined;
      if (!row?.clinic_id) return null;

      // 4. Lookup do owner member pra saber se já aceitou.
      const memberRes = await supabase
        .from('clinic_members')
        .select('status')
        .eq('clinic_id', row.clinic_id)
        .eq('role_key', 'clinic_owner')
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      const ownerMemberStatus =
        (memberRes.data?.status as 'invited' | 'active' | 'suspended' | undefined) ?? null;

      // 5. Lookup da subscription pra distinguir gate de pagamento
      // (incomplete) vs aguardando aceite do convite (active).
      const subRes = await supabase
        .from('subscriptions')
        .select('status')
        .eq('clinic_id', row.clinic_id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const subscriptionStatus =
        (subRes.data?.status as
          | 'incomplete'
          | 'trialing'
          | 'active'
          | 'past_due'
          | 'canceled'
          | 'paused'
          | undefined) ?? null;

      return {
        clinic_id: row.clinic_id,
        owner_member_status: ownerMemberStatus,
        subscription_status: subscriptionStatus,
      };
    },
  });
}

/**
 * Dispara o edge function `provision-clinic` — cria clinic + member +
 * Asaas customer/subscription + envia email welcome ao dono. Chamado
 * pelo botão "Provisionar agora" do `LeadCustomerBanner` quando o
 * lead atinge `stage='signed'` (contrato assinado mas clínica ainda
 * não criada).
 *
 * **Idempotente no backend** — chamada duplicada retorna `mode='cached'`
 * com o clinic_id existente, sem criar nada novo. UI segura mesmo
 * em duplo-clique.
 *
 * Após sucesso: invalida lead detail (stage transita pra `provisioned`)
 * + activities (entry `provisioning_completed` aparece na timeline).
 */
export interface ProvisionClinicResult {
  status: string;
  mode: 'live' | 'stub' | 'cached' | 'failed';
  clinic_id?: string;
  subscription_id?: string;
  activation_url?: string;
  step?: string;
  reason?: string;
}

export function useProvisionClinic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      lead_id: string;
      contract_id: string;
      force?: boolean;
    }): Promise<ProvisionClinicResult> => {
      const { data, error } = await supabase.functions.invoke('provision-clinic', {
        body: {
          contract_id: input.contract_id,
          force: input.force ?? false,
        },
      });
      if (error) throw new Error(error.message);
      return data as ProvisionClinicResult;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.detail(variables.lead_id) });
      qc.invalidateQueries({ queryKey: LEAD_KEYS.activities(variables.lead_id) });
      qc.invalidateQueries({ queryKey: ['leads', 'list'] });
    },
  });
}
